import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ArrowLeft, Save, X } from "lucide-react";
import {
  createSubCategory,
  updateSubCategory,
  fetchSubCategoryById,
  resetSubCategoryStatuses,
} from "../../store/slices/subCategoriesSlice";
import { fetchCategories } from "../../store/slices/commonApiSlice";
import { TextField, CheckboxField } from "../../components/ui/FormControls";
import { ImageDropField } from "../../components/ui/ImageDropField";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Loader from "../../components/Loader";

const subCategorySchema = yup.object({
  category_uuid: yup
    .string()
    .required("Category is required"),
  name: yup
    .string()
    .required("SubCategory name is required")
    .max(255, "SubCategory name must be less than 255 characters"),
  // tagline: yup
  //   .string()
  //   .nullable()
  //   .max(500, "Tagline must be less than 500 characters"),
  // icon: yup.mixed().nullable(),
  status: yup.boolean(),
});

export default function AddSubCategoryPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const editUuid = searchParams.get("edit");

  const [iconFile, setIconFile] = useState(null);
  const [existingIcon, setExistingIcon] = useState(null);

  const {
    subCategory,
    subCategoryCreateStatus,
    subCategoryUpdateStatus,
    subCategoryFetchStatus,
    loading,
  } = useSelector((state) => state.subCategories);

  const { categories = [] } = useSelector((state) => state.common);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(subCategorySchema),
    defaultValues: {
      category_uuid: "",
      name: "",
      // tagline: "",
      status: true,
    },
  });

  // Fetch categories for dropdown
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  // Fetch subcategory data if editing
  useEffect(() => {
    if (editUuid) {
      dispatch(fetchSubCategoryById(editUuid));
    }
  }, [editUuid, dispatch]);

  // Populate form when subcategory data is loaded
  useEffect(() => {
    if (editUuid && subCategory && subCategoryFetchStatus === "success") {
      reset({
        category_uuid: subCategory.category?.uuid || "",
        name: subCategory.name || "",
      // tagline: subCategory.tagline || "",
        status: Boolean(subCategory.status),
      });
      // if (subCategory.icon) {
      //   setExistingIcon(subCategory.icon);
      // }
    }
  }, [subCategory, subCategoryFetchStatus, editUuid, reset]);

  const onSubmit = async (formData) => {
    try {
      const payload = new FormData();
      payload.append("category_uuid", formData.category_uuid);
      payload.append("name", formData.name);
      payload.append("status", formData.status ? "1" : "0");
      
      // Add tagline if provided
      // if (formData.tagline) {
      //   payload.append("tagline", formData.tagline);
      // }

      // Add icon if new file is selected
      // if (iconFile && iconFile.length > 0) {
      //   payload.append("icon", iconFile[0]);
      // }

      if (editUuid) {
        await dispatch(updateSubCategory({ uuid: editUuid, data: payload })).unwrap();
        showApiSuccess("SubCategory updated successfully");
      } else {
        await dispatch(createSubCategory(payload)).unwrap();
        showApiSuccess("SubCategory created successfully");
      }

      navigate("/sub-categories");
    } catch (error) {
      showApiError(null, error || `Failed to ${editUuid ? "update" : "create"} subcategory`);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetSubCategoryStatuses());
    };
  }, [dispatch]);

  const isSubmitting =
    subCategoryCreateStatus === "loading" || subCategoryUpdateStatus === "loading";

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {loading && <Loader />}

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/sub-categories")}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {editUuid ? "Edit SubCategory" : "Add New SubCategory"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {editUuid
                  ? "Update subcategory information"
                  : "Create a new subcategory linked to a category"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Basic Information
          </h3>

          <div className="space-y-4">
            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parent Category <span className="text-red-500">*</span>
              </label>
              <select
                {...register("category_uuid")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.uuid} value={category.uuid}>
                    {category.name}
                  </option>
                ))}
              </select>
              {errors?.category_uuid && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.category_uuid.message}
                </p>
              )}
            </div>

            {/* SubCategory Name */}
            <div>
              <TextField
                label="SubCategory Name"
                required
                placeholder="Enter subcategory name"
                {...register("name")}
                error={errors?.name?.message}
              />
            </div>

            {/* Tagline — commented out, not required for now */}
            {/* <div>
              <TextField
                label="Tagline"
                placeholder="Enter subcategory tagline (optional)"
                {...register("tagline")}
                error={errors?.tagline?.message}
              />
              <p className="text-sm text-gray-500 mt-1">
                A short description or tagline for this subcategory (max 500 characters)
              </p>
            </div> */}

            {/* Status */}
            <div>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <CheckboxField
                    label="Active Status"
                    checked={Boolean(field.value)}
                    onChange={(checked) => field.onChange(checked)}
                  />
                )}
              />
              <p className="text-sm text-gray-500 mt-1">
                Enable this subcategory to make it visible to users
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 mt-6">
            <button
              type="button"
              onClick={() => navigate("/sub-categories")}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              <span>
                {isSubmitting
                  ? "Saving..."
                  : editUuid
                  ? "Update SubCategory"
                  : "Create SubCategory"}
              </span>
            </button>
          </div>
        </div>

        {/* SubCategory Icon — commented out, not required for now */}
        {/* <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            SubCategory Icon
          </h3>

          {existingIcon && !iconFile && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Icon
              </label>
              <div className="relative inline-block">
                <img
                  src={existingIcon}
                  alt="Current subcategory icon"
                  className="w-32 h-32 rounded-lg object-cover border-2 border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => setExistingIcon(null)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Upload a new icon to replace the current one
              </p>
            </div>
          )}

          <div>
            <ImageDropField
              label={existingIcon && !iconFile ? "Upload New Icon" : "Upload Icon"}
              value={iconFile}
              onChange={setIconFile}
              hint="Drop an icon image or click to browse (JPEG, PNG, JPG, WEBP - Max 5MB)"
              maxFiles={1}
              accept="image/jpeg,image/png,image/jpg,image/webp"
            />
          </div>
        </div> */}

        {/* Form Actions merged into Basic Information card above */}
      </form>
    </div>
  );
}
